import React, {JSXElementConstructor, useEffect, useState} from 'react';
import ReactSelect from 'react-select';
import ReactCreatableSelect from 'react-select/creatable';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';

import {Choice, Field, FieldProp} from '../../types';
import {DisplayOnly} from './DisplayOnly';

import {loadAsyncOptions, handleAsyncOnChange} from '../../_helpers/select';
import {FormState} from '../Form';
import {Etl} from '../../_services/Etl';
import {applyFillMap} from '../../_helpers/etl';
import debounce from 'debounce-promise';

type SelectProps = FieldProp & {
    isMulti?: boolean;
    onInputChange?: (value: string, action: string) => void;
    inputValue?: string;
    placeholder?: string;
};

const htmlFormatOption = (option: any) => (
    <div dangerouslySetInnerHTML={{__html: option.label}}/>
);

const reRenderHandlerMap: any = {};
const reRenderHandler = (field: any) => {
    if (!reRenderHandlerMap.hasOwnProperty(field.id)) {
        reRenderHandlerMap[field.id] = debounce((reRender: any) => {
            console.log('Select component - calling async options with field and state', field.id);
            reRender();
        }, 250);
    }
    return reRenderHandlerMap[field.id];
}

export const Select: React.FunctionComponent<SelectProps> = ({
                                                                 field,
                                                                 formState,
                                                                 fieldState,
                                                                 value,
                                                                 displayOnly,
                                                                 onChange,
                                                                 onBlur,
                                                                 isMulti = false,
                                                                 placeholder,
                                                                 onInputChange,
                                                                 inputValue,
                                                                 ...additionalProps
                                                             }): JSX.Element => {

    // use the renderKey to force the dropdown to call loadOptions whenever there is a formState change
    const [renderKey, setRenderKey] = useState<number>(1);
    const loadAsyncOptionsHandler = loadAsyncOptions(field, formState);
    const hasAsyncConfig: boolean = !!field?.etlConfig?.pipeline;
    const refreshAsyncDefaultOptions: boolean = field?.flowCustomDisplayComponentConfig?.refreshDefaultAsyncOptionsOnStateChange || false;

    const handleFormStateChange = reRenderHandler(field);

    useEffect(() => {
        if (!displayOnly && hasAsyncConfig && refreshAsyncDefaultOptions) {
            console.log('Select component - calling do something', field.id);
            handleFormStateChange(() => setRenderKey(renderKey + 1));
        }
    }, [formState]);

    const {
        touched,
        pristine,
        validating,
        valid,
        invalid,
        errors,
    } = fieldState;
    const classNames = [
        /* 'form-control', */
        touched && 'is-touched',
        pristine && 'is-pristine',
        validating && 'is-validating',
        valid && 'is-valid',
        invalid && 'is-invalid',
    ]
        .filter(Boolean)
        .join(' ');

    let AsyncComponentDefaultOptions = (value) ? isMulti ? value : [value] : [];
    if (hasAsyncConfig && field?.etlConfig?.loadOptionsByDefault === true) {
        AsyncComponentDefaultOptions = true;
    }

    let ComponentProps: any = {
        id: field.htmlId,
        required: field.isRequired,
        className: classNames,
        isMulti: isMulti,
        value: value,
        defaultValue: value,
        isDisabled: field.gwreadonly_enable,
        /* menuPosition: 'fixed', */ // temporary to see if it solves my issues in nestedformgrid
        onBlur: () => {
            // React-Select on mobile fired onChange and onBlur simulatenously, onBlur depends on the value of the select having
            // been set. To fix, call onBlur with currently selected option after onChange
            // onBlur && onBlur();
        },
        placeholder: field.placeholder ? field.placeholder : placeholder ? placeholder : 'Select...'
    };

    let SelectComponent: undefined | JSXElementConstructor<any> = undefined;

    const advancedFilter = (formState: FormState | undefined, field: Field) => {
        if (field?.flowCustomDisplayComponentConfig?.filterFunction) {
            // @ts-ignore - item used within filterFunction
            return (item: Choice) => {
                let result = false;
                try {
                    // tslint:disable-next-line:no-eval
                    result = eval(field.flowCustomDisplayComponentConfig.filterFunction);
                } catch (e) {
                    console.log('Exception', e);
                    result = false;
                }
                return result;
            };
        } else {
            return () => true;
        }
    };

    const fieldChoices = field.choices.filter(advancedFilter(formState, field)).map(c => ({...c, text: c.text.replace('&amp;', '&'), label: c.label.replace('&amp;', '&')}));

    fieldChoices.map(i => {
        return i;
    });

    if (hasAsyncConfig === false) {
        SelectComponent = ReactSelect;
        ComponentProps = {
            ...ComponentProps,
            // @ts-ignore
            onChange: (option, props) => {
                onChange(option);

                if (field?.flowCustomDisplayComponentConfig?.onChangePipeline) {
                    const etlConfigs = field?.flowCustomDisplayComponentConfig?.onChangePipeline;
                    etlConfigs.forEach((etlConfig) => {
                        console.log('etlConfig', etlConfig);
                        const resultProperty = etlConfig.resultProperty || 'result';
                        const requestValueProperty = etlConfig?.requestValueProperty || 'q';
                        const additionalRequestProperties = etlConfig?.additionalRequestProperties || [];
                        const requestObject = {
                            [requestValueProperty]: option.value,
                            ...additionalRequestProperties,
                            // tslint:disable-next-line:no-eval
                            ...(etlConfig?.additionalRequestPropertiesCallback ? eval(etlConfig.additionalRequestPropertiesCallback) : {})
                        };

                        Etl.getInstance().process(etlConfig.pipeline, requestObject)
                            .then((response) => {
                                if (response.ok) {
                                    let result: object | object[] = response.json[resultProperty];
                                    if (result instanceof Array) {
                                        result = result.length > 0 ? result[0] : {};
                                    }
                                    const stateUpdates = applyFillMap(etlConfig.fillMap, result);
                                    onChange && onChange(option, stateUpdates);
                                }
                            })
                            .catch((error: any) => {
                                console.log(error);
                            });
                    });
                }

                // React-Select on mobile fires onChange and onBlur simultaneously, onBlur depends on the value of the select having
                // been set. To fix, call onBlur with currently selected option after onChange
                setTimeout(() => {
                    onBlur && onBlur({[field.name]: option});
                }, 2);
            },
            options: fieldChoices,
        }

        if (field?.flowCustomDisplayComponentConfig?.allowTags) {
            SelectComponent = ReactCreatableSelect;
            ComponentProps = {
                ...ComponentProps,
                createOptionPosition: 'first',
                getNewOptionData: (userInput: string) => ({
                    text: userInput,
                    label: userInput,
                    value: userInput
                }),
                isClearable: true
            };
        }
    } else {
        SelectComponent = (hasAsyncConfig && field.etlConfig.allowTags) ? AsyncCreatableSelect : AsyncSelect;
        ComponentProps = {
            ...ComponentProps,
            // @ts-ignore
            onChange: (option, props) => {
                handleAsyncOnChange(field, onChange, onBlur)(option);
            },
            defaultOptions: AsyncComponentDefaultOptions,
            loadOptions: loadAsyncOptionsHandler,
            isClearable: true,
        };

        if (hasAsyncConfig && field.etlConfig.allowTags) {
            ComponentProps = {
                ...ComponentProps,
                createOptionPosition: 'first',
                getNewOptionData: (userInput: string) => ({
                    text: userInput,
                    label: userInput,
                    value: userInput
                })
            };
        }

        if (field?.etlConfig?.htmlOptionText) {
            ComponentProps = {
                ...ComponentProps,
                formatOptionLabel: htmlFormatOption
            };
        }
    }

    if (additionalProps?.formatOptionLabel) {
        ComponentProps = {
            ...ComponentProps,
            formatOptionLabel: additionalProps.formatOptionLabel
        }
    }

    if (onInputChange) {
        ComponentProps = {
            ...ComponentProps,
            onInputChange: onInputChange,
            inputValue: inputValue
        };
    }

    return (
        <div>
            {displayOnly ?
                <DisplayOnly>{value?.label}</DisplayOnly>
                :
                <SelectComponent {...ComponentProps} key={renderKey}/>
            }
            {errors && errors.map((error: string, index: number) => (
                <div key={index} className="invalid-feedback">
                    {error}
                </div>
            ))}
        </div>
    );
};